import { useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Seo, PageHeader } from "@/components/common";

const LEGAL = {
  kvkk: {
    title: { tr: "KVKK Aydınlatma Metni", en: "KVKK Notice" },
    body: {
      tr: [
        "Zeta Clinic olarak, 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) kapsamında veri sorumlusu sıfatıyla hareket ediyoruz. Ayrıca Avrupa Birliği'nden hasta verisi işlediğimiz için Genel Veri Koruma Tüzüğü (GDPR) yükümlülüklerine de tabiyiz.",
        "İşlenen veriler: ad-soyad, iletişim bilgileri, ülke ve dil tercihi ile — açık rızanızla — paylaştığınız sağlık verileri (fotoğraf ve belgeler). Sağlık verileri özel nitelikli kişisel veridir ve yalnızca açık rızanız ile, değerlendirme amacıyla işlenir.",
        "Yurt dışı aktarım: Verileriniz, tedavi koordinasyonu amacıyla Türkiye'de işlenir. Yurt dışına aktarım yalnızca açık rızanız kapsamında yapılır.",
        "Saklama süresi: Verileriniz, ilgili mevzuatta öngörülen süreler boyunca saklanır ve süre sonunda silinir veya anonim hale getirilir.",
        "Haklarınız: KVKK madde 11 kapsamında verilerinize erişme, düzeltme, silme talep etme ve işlemeye itiraz etme hakkına sahipsiniz. Talepleriniz için info@zetaclinic.org adresine yazabilirsiniz.",
        "Bu metin taslaktır; yayına alınmadan önce sağlık hukuku danışmanı tarafından onaylanmalıdır.",
      ],
      en: [
        "As Zeta Clinic, we act as data controller under Turkish Law No. 6698 on the Protection of Personal Data (KVKK). As we process patient data from the European Union, we are also subject to GDPR obligations.",
        "Data processed: full name, contact details, country and language preference, and — with your explicit consent — health data you share (photos and documents). Health data is special-category personal data and is processed solely with your explicit consent, for evaluation purposes.",
        "International transfer: Your data is processed in Türkiye for treatment coordination. Transfers abroad take place only within the scope of your explicit consent.",
        "Retention: Your data is retained for the periods required by applicable law and is then deleted or anonymised.",
        "Your rights: You may request access, rectification, deletion and object to processing. Contact info@zetaclinic.org for requests.",
        "This text is a draft; it must be approved by a health law adviser before launch.",
      ],
    },
  },
  gizlilik: {
    title: { tr: "Gizlilik Politikası", en: "Privacy Policy" },
    body: {
      tr: [
        "Bu web sitesi üzerinden paylaştığınız kişisel veriler yalnızca sağlık koordinasyonu amacıyla kullanılır; üçüncü taraflara pazarlama amacıyla aktarılmaz.",
        "Yüklediğiniz fotoğraf ve belgeler şifreli olarak saklanır, herkese açık bağlantılarla erişilemez ve yalnızca yetkili sağlık personeli tarafından görüntülenir.",
        "Verileriniz e-posta ile düz metin olarak iletilmez. Erişim yetkileri kayıt altındadır.",
        "Bu metin taslaktır; yayına alınmadan önce hukukçu onayı gereklidir.",
      ],
      en: [
        "Personal data shared through this website is used solely for healthcare coordination; it is not transferred to third parties for marketing.",
        "Uploaded photos and documents are stored encrypted, are not accessible via public links, and are viewed only by authorised medical staff.",
        "Your data is never sent as plain text via email. Access permissions are logged.",
        "This text is a draft; legal approval is required before launch.",
      ],
    },
  },
  cerez: {
    title: { tr: "Çerez Politikası", en: "Cookie Policy" },
    body: {
      tr: [
        "Sitemiz şu an yalnızca zorunlu (işlevsel) çerezler kullanır: dil tercihi, rehber seçimi ve çerez onay durumu tarayıcınızda saklanır.",
        "Onayınız olmadan hiçbir takip veya pazarlama çerezi çalıştırılmaz. Analitik araçlar devreye alındığında bu politika güncellenecek ve onayınız yeniden istenecektir.",
        "Çerez tercihlerinizi tarayıcı ayarlarınızdan her zaman silebilirsiniz.",
      ],
      en: [
        "Our site currently uses only essential (functional) cookies: language preference, guide selection and cookie consent status are stored in your browser.",
        "No tracking or marketing cookies run without your consent. When analytics tools are activated, this policy will be updated and your consent requested again.",
        "You can always clear your cookie preferences via your browser settings.",
      ],
    },
  },
  "kullanim-kosullari": {
    title: { tr: "Kullanım Koşulları", en: "Terms of Use" },
    body: {
      tr: [
        "Bu web sitesindeki tüm içerik bilgilendirme amaçlıdır; tıbbi tavsiye niteliği taşımaz, tanı koymaz ve tedavi sonucu garanti etmez.",
        "Nihai tedavi planı ve uygunluk, hekimin klinik değerlendirmesi sonucunda belirlenir.",
        "Site içeriği Zeta Clinic'e aittir; izinsiz kopyalanamaz.",
        "Bu metin taslaktır; yayına alınmadan önce hukukçu onayı gereklidir.",
      ],
      en: [
        "All content on this website is for information purposes only; it does not constitute medical advice, does not diagnose, and does not guarantee treatment outcomes.",
        "The final treatment plan and suitability are determined by the dentist's clinical evaluation.",
        "Site content belongs to Zeta Clinic and may not be copied without permission.",
        "This text is a draft; legal approval is required before launch.",
      ],
    },
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const { t, lang } = useApp();
  const page = LEGAL[slug] || LEGAL.kvkk;
  return (
    <main data-testid={`legal-page-${slug}`}>
      <Seo title={t(page.title)} description={t(page.title)} />
      <PageHeader tag={{ tr: "Yasal", en: "Legal" }} title={page.title} />
      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-8">
        <div className="rounded-2xl glass-card p-8 md:p-12 space-y-5">
          {page.body[lang === "en" ? "en" : "tr"].map((p, i) => (
            <p key={i} className="text-sm text-[#94A3AE] leading-relaxed">{p}</p>
          ))}
        </div>
      </section>
    </main>
  );
}
